// @ts-strict-ignore
import {
  Component,
  Input,
  forwardRef,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppDateAdapter, APP_DATE_FORMATS } from './date-picker.adapter';

@UntilDestroy()
@Component({
    selector: 'date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true,
        },
        {
            provide: DateAdapter,
            useClass: AppDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: APP_DATE_FORMATS,
        },
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DatePickerComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() public options = {}; // WILL REMOVE
  @Input() public placeholder = 'Choose a date';
  @Input() public inputHeight = '2.125rem';
  @Input() public marginTop = '';
  @Input() public allowTyping = true;
  @Input() public disableControl = false;
  @Input() public inline: boolean = false;
  @Input() public minDate: Date | null = new Date(2000, 0, 1);
  @Input() public maxDate: Date | null = null;
  @Input() public disableWeekends = false;
  @Input() public customFilter: (date: Date) => boolean = null;
  @Input() public hasError = false;
  @Input() public readonly defaultBorderColour: string = '#969696';

  // Allow tracking of blur and focus events
  @Output() public blur = new EventEmitter<any>();
  @Output() public focus = new EventEmitter<any>();
  @Output() public dateChanged = new EventEmitter<Date>();

  public dateControl = new FormControl('');
  public onTouched: () => void = () => {};
  public matFilter = null;
  public borderColour: string;
  public constructor() {}

  ngOnInit() {
    this.borderColour = this.defaultBorderColour;
    this.changeMatFilter();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.borderColour = this.hasError ? 'var(--red-error)' : this.defaultBorderColour;

    if (changes.disableWeekends || changes.customFilter) {
      this.changeMatFilter();
    }

    if (changes.disableControl) {
      this.setDisabledState(changes.disableControl.currentValue);
    }
  }

  public handleBlur(event: any): void {
    this.blur.emit(event);
  }

  public handleFocus(event: any): void {
    this.focus.emit(event);
  }

  /**
   * changeMatFilter if input is changed and on first load
   * @param any obj
   * @return void
   */
  private changeMatFilter(): void {
    if (this.customFilter != null) {
      this.matFilter = this.customFilter;
    } else if (this.disableWeekends) {
      // Not saturday or sunday
      this.matFilter = (date: Date) => !(date.getDay() === 6 || date.getDay() === 0);
    } else {
      this.matFilter = null;
    }
  }

  /**
   * When the form's value written to. Implemented from the ControlValueAccessor interface
   * @param any obj
   * @return void
   */
  public writeValue(obj: any): void {
    this.dateControl.patchValue(obj, { onlySelf: true, emitEvent: false });
  }

  /**
   * When the form's value is changed. Implemented from the ControlValueAccessor interface
   * @param any fn
   * @return void
   */
  public registerOnChange(fn: any): void {
    this.dateControl.valueChanges.pipe(untilDestroyed(this)).subscribe(fn);
  }

  /**
   * When the form is touched. Implemented from the ControlValueAccessor interface
   * @param any fn
   * @return void
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Whether the form is disabled. Implemented from the ControlValueAccessor interface
   * @param boolean isDisabled
   * @return void
   */
  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.dateControl.disable() : this.dateControl.enable();
  }

  /**
   * Checks if the form control has a value
   * @return boolean
   */
  public valueExists(): boolean {
    return !!this.dateControl.value;
  }

  /**
   * Emits to the parent on date change
   * @param any event
   * @return void
   */
  public onDateChanged(event): void {
    this.dateChanged.emit(event.value);
  }

  public onInlineDateChanged(event): void {
    this.dateControl.setValue(event);
    this.dateChanged.emit(event);
  }

  /**
   * Decides whether to toggle calendar when clicking on input
   *
   * @param any Date picker element reference
   *
   * @return void
   */
  public onInputClick(dp: any): void {
    if (!(this.allowTyping || this.disableControl || this.inline)) {
      if (dp.opened) {
        dp.open();
      } else {
        dp.close();
      }
    }
  }
}
